import trajectoryData from './trajectoryData.json';
import coordtransform from '@/WGS84.js';

// 将 gtm（YYYYMMDD/HHmmss）转换为毫秒时间戳
function gtmToTimestamp(gtm) {
  if (!gtm || typeof gtm !== 'string') return '';
  const [ymd, hms] = gtm.split('/');
  if (!ymd || !hms || ymd.length !== 8 || hms.length !== 6) return '';
  const y = Number(ymd.slice(0, 4));
  const m = Number(ymd.slice(4, 6));
  const d = Number(ymd.slice(6, 8));
  const hh = Number(hms.slice(0, 2));
  const mm = Number(hms.slice(2, 4));
  const ss = Number(hms.slice(4, 6));
  const ts = new Date(y, m - 1, d, hh, mm, ss).getTime();
  return Number.isFinite(ts) ? String(ts) : '';
}

function timestampToGtm(ts) {
  const n = Number(ts);
  if (!Number.isFinite(n)) return '';
  const ms = n < 1e12 ? n * 1000 : n;
  const d = new Date(ms);
  if (Number.isNaN(d.getTime())) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  const ss = String(d.getSeconds()).padStart(2, '0');
  return `${y}${m}${day}/${hh}${mm}${ss}`;
}

function normalizeWgs84LonLat(rawLon, rawLat, options = {}) {
  const { divisor = 600000, convertGcj02ToWgs84 = false } = options;
  const nlon = Number(rawLon);
  const nlat = Number(rawLat);
  if (!Number.isFinite(nlon) || !Number.isFinite(nlat)) return ['', ''];
  const useScaled = Math.max(Math.abs(nlon), Math.abs(nlat)) > 10000;
  // 与 RightData* 一致：scaled 数据视为 WGS84（×600000），先转十进制度再转 GCJ02；
  // 未 scaled 数据按 GCJ02 直出，保证卡片与地图使用同源坐标。
  if (useScaled) {
    const lonWgs84 = nlon / divisor;
    const latWgs84 = nlat / divisor;
    if (convertGcj02ToWgs84) return [lonWgs84, latWgs84];
    return coordtransform.wgs84togcj02(lonWgs84, latWgs84);
  }
  if (!convertGcj02ToWgs84) return [nlon, nlat];
  const [lonWgs84, latWgs84] = coordtransform.gcj02towgs84(nlon, nlat);
  return [lonWgs84, latWgs84];
}

function toTracePointString(point, options = {}) {
  const [lon, lat] = normalizeWgs84LonLat(point.lon, point.lat, options);
  const spd = point.spd == null ? '' : String(point.spd);
  const gtm = point.gtm || timestampToGtm(point.gpsTime || point.utc) || '';
  const gpsTime = String(point.gpsTime || gtmToTimestamp(gtm) || point.utc || '');
  const acc = String(point.acc == null ? 1 : point.acc);
  const utc = String(point.utc || gpsTime || '');
  const angle = point.agl == null ? String(point.angle || '') : String(point.agl);
  const roadLimitSpd = point.roadLimitSpd == null ? '' : String(point.roadLimitSpd);
  const type = point.type == null ? '0' : String(point.type);
  // 追加 hgt（海拔）：用于轨迹地图信息窗展示；老版本 parseTracePoints 不读该字段也不受影响
  const hgt = point.hgt == null || point.hgt === '' ? '' : String(point.hgt);
  const vclN = point.vclN != null ? String(point.vclN).trim() : '';
  return `${lon}:${lat}:${spd}:${gpsTime}:${gtm}:${acc}:${utc}:${angle}:${roadLimitSpd}:${type}:${hgt}:${vclN}`;
}

function convertParkArrayToTrackParkingDetailList(parkArray, options = {}) {
  if (!Array.isArray(parkArray) || parkArray.length === 0) return [];
  return parkArray.map((p) => {
    const beginGpsTime = Number(p.parkBte) < 1e12 ? Number(p.parkBte) * 1000 : Number(p.parkBte);
    const endGpsTime = Number(p.parkEte) < 1e12 ? Number(p.parkEte) * 1000 : Number(p.parkEte);
    const [beginLon, beginLat] = normalizeWgs84LonLat(p.parkLon, p.parkLat, options);
    return {
      beginGpsTime,
      endGpsTime,
      startGtm: timestampToGtm(beginGpsTime),
      endGtm: timestampToGtm(endGpsTime),
      beginLon: String(beginLon),
      endLon: String(beginLon),
      beginLat: String(beginLat),
      endLat: String(beginLat),
      parkTime: Math.max(0, Math.floor((endGpsTime - beginGpsTime) / 1000)),
      vclN: p.vclN != null ? String(p.vclN).trim() : '',
      parkAdr: p.parkAdr != null ? String(p.parkAdr).trim() : '',
    };
  });
}

export function convertRunRouteToTrackData(runRoute, extra = {}, options = {}) {
  const list = Array.isArray(runRoute) ? runRoute : [];
  const traceList = list.map((p) => toTracePointString(p, options));
  const trackParkingDetailList = convertParkArrayToTrackParkingDetailList(extra.parkArray, options);
  const totalStopTime = trackParkingDetailList.reduce((sum, p) => sum + (Number(p.parkTime) || 0), 0);
  const maxSpeed = list.reduce((max, p) => Math.max(max, Number(p.spd) || 0), 0);
  const first = list[0];
  const last = list[list.length - 1];
  const totalTimes = first && last
    ? Math.max(0, Number(gtmToTimestamp(last.gtm)) - Number(gtmToTimestamp(first.gtm)))
    : 0;
  return {
    traceList,
    traceOverviewInfo: {
      totalMileage: Number(extra.totalMileage || extra.mileage || 0),
      totalTimes: Number.isFinite(totalTimes) ? totalTimes : 0,
      maxSpeed,
      avgSpeed: Number(extra.avgSpeed || 0)
    },
    parkInfo: {
      trackParkingDetailList,
      totalStopTime,
      stopTimes: trackParkingDetailList.length
    }
  };
}

// 支持直接传 historicalTrajectoryNew 中构造的 routePayload 或接口原始 body.data
export function convertTrajectoryPayloadToTrackData(payload, options = {}) {
  const p = payload && typeof payload === 'object' ? payload : {};
  const runRoute = Array.isArray(p.runRoute) ? p.runRoute : [];
  const parkArray = Array.isArray(p.parkArray) ? p.parkArray : [];
  return convertRunRouteToTrackData(runRoute, {
    parkArray,
    totalMileage: p.mileage || p.num || 0,
    avgSpeed: p.avgSpeed || 0
  }, options);
}

// 保留默认导出：把 trajectoryData.json 转成同结构（兼容已有引用）
export const trajectoryTrackData = convertRunRouteToTrackData(trajectoryData?.data?.runRoute || [], {
  totalMileage: trajectoryData?.data?.mil || 0
}, {
  convertGcj02ToWgs84: true
});

export default trajectoryTrackData;
